import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import { WA_LINK, scrollToId } from "@/lib/contact";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Vision & Mission", href: "#mission" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact Us", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#6CBE45]/10 shadow-[0_4px_30px_-8px_rgba(108,190,69,0.15)]"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[70px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => scrollToId(e, "#top")}
          className="flex items-center gap-3 group"
          data-testid="navbar-brand"
        >
          <img
            src="/assets/logo-round.png"
            alt="PK Hygiene logo"
            className="w-11 h-11 rounded-full ring-2 ring-[#D4AF37]/60 group-hover:ring-[#6CBE45] transition-shadow duration-300"
          />
          <div className="leading-none">
            <span className="block font-extrabold tracking-tight text-lg">
              PK <span className="text-[#6CBE45]">Hygiene</span>
            </span>
            <span className="block text-[10px] font-mono-accent uppercase tracking-[0.22em] text-[#4A5A5E] mt-0.5">
              Deep Cleaning Services
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="navbar-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollToId(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[\s/&]+/g, "-")}`}
              className="text-sm font-medium text-[#4A5A5E] hover:text-[#4A8F2A] transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-[#D4AF37] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#1eb857] transition-colors duration-300 shadow-[0_8px_24px_-6px_rgba(37,211,102,0.5)]"
          >
            <MessageCircle className="w-4 h-4" />
            Book Now
          </a>
          <button
            className="lg:hidden p-2 text-[#0F1A1C]"
            onClick={() => setOpen(!open)}
            data-testid="navbar-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-[#6CBE45]/10"
            data-testid="navbar-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    scrollToId(e, l.href);
                    setOpen(false);
                  }}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[\s/&]+/g, "-")}`}
                  className="text-base font-medium text-[#0F1A1C]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="navbar-mobile-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-5 py-3"
              >
                <MessageCircle className="w-4 h-4" />
                Book Now on WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
